import { useEffect, useState } from "react";
import { Banner, Button, Card, CardGrid, Cell, Group, Header, Placeholder, Text, Title } from "@vkontakte/vkui";
import { Icon24ArticleOutline, Icon24MarketOutline, Icon24UserAddOutline } from "@vkontakte/icons";
import type { MeResponse, Partner } from "../api/types";
import { api } from "../api/client";
import { formatDate, formatMoney } from "../shared/format";
import { Page } from "../components/Page";
import { MainNav } from "../components/Nav";
import { PartnerMedia } from "../components/PartnerMedia";

export function HomePage({ me, navigate }: { me: MeResponse | null; navigate: (path: string) => void }) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const vkAppUrl = import.meta.env.VITE_VK_APP_URL || "https://vk.com/app54589416";

  useEffect(() => {
    api
      .get<{ partners: Partner[] }>("/partners")
      .then((response) => setPartners(response.data.partners.slice(0, 4)))
      .catch(() => setPartners([]));
  }, []);

  const role = me?.user.role || "guest";
  const membership = me?.membership;
  const isPartner = Boolean(me?.partners?.length) || role === "super_admin";
  const isAdmin = ["super_admin", "member_manager", "partner_manager"].includes(role);

  return (
    <Page title="Клубная карта">
      <MainNav user={me?.user ?? null} hasMembership={Boolean(membership)} hasPartnerAccess={Boolean(me?.partners?.length)} navigate={navigate} />

      {role === "guest" ? (
        <Group>
          <Placeholder
            header="Клуб, партнёры и скидки"
            action={
              <div className="toolbar centered">
                <Button size="l" href={vkAppUrl} target="_blank" rel="noreferrer">
                  Войти через VK
                </Button>
                <Button size="l" mode="secondary" before={<Icon24UserAddOutline />} onClick={() => navigate("/legal/consent")}>
                  Стать членом клуба
                </Button>
              </div>
            }
          >
            Откройте приложение во ВКонтакте, чтобы загрузить профиль, членскую карту, скидки и кабинет по вашей роли. Без входа доступен публичный каталог партнёров.
          </Placeholder>
        </Group>
      ) : null}

      {membership ? (
        <Group header={<Header>Моя карта</Header>}>
          <div className="club-card">
            <Title level="2">{me?.user.fullName}</Title>
            <Text>Уровень {membership.level} · действует до {formatDate(membership.expiresAt)}</Text>
            <div className="club-card-number">{membership.cardNumber}</div>
            <Text>Экономия: {formatMoney(membership.totalSaved)}</Text>
          </div>
          <div className="toolbar" style={{ marginTop: 12 }}>
            <Button before={<Icon24ArticleOutline />} onClick={() => navigate("/membership")}>
              Открыть карту
            </Button>
            <Button mode="secondary" before={<Icon24MarketOutline />} onClick={() => navigate("/partners")}>
              Партнёры
            </Button>
          </div>
        </Group>
      ) : null}

      {isPartner ? (
        <Group>
          <Banner
            header="Кабинет партнёра"
            subheader={
              me?.partners?.length
                ? `Доступно организаций: ${me.partners.length}. Активная: ${me.partner?.title ?? me.partners[0].title}`
                : "Доступ к партнёрским инструментам"
            }
            actions={<Button onClick={() => navigate("/partner")}>Открыть кабинет</Button>}
          />
        </Group>
      ) : null}

      {isAdmin ? (
        <Group>
          <Banner
            header="Администрирование"
            subheader="Управление членами клуба, партнёрами, ролями и аудитом"
            actions={<Button mode="secondary" onClick={() => navigate("/admin")}>Перейти в админку</Button>}
          />
        </Group>
      ) : null}

      <Group header={<Header>Партнёры и акции</Header>}>
        <CardGrid size="l">
          {partners.map((partner) => (
            <Card key={partner.id} mode="shadow" onClick={() => navigate(`/partners/${partner.id}`)}>
              <PartnerMedia title={partner.title} category={partner.category} logoUrl={partner.logoUrl} coverUrl={partner.coverUrl} />
              <Cell
                subtitle={`${partner.category} · ${partner.discountBenefitLabel || "специальные условия"}`}
                after={partner.hasActiveOffer ? "Акция" : partner.isPromoted ? "Продвигается" : undefined}
              >
                {partner.title}
              </Cell>
            </Card>
          ))}
        </CardGrid>
      </Group>
    </Page>
  );
}
